import { computed, observable } from 'mobx';
import { validate as uuidValidate } from 'uuid';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { CXBaseBlock } from './cx-base-block.model';
import { IContractHTMLBlockResponse } from '~/types/server.types';
import { IContract, ISmartField } from '~/types/model.types';
import { ContractBlockErrors } from '~/types/error.types';
import { CXQuestionBlock } from './cx-question-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import { previewVSmartFieldsValues } from '~/components/Builder/BuilderSidebar/ContractSidebar/ContractHtml.utils';

export class CXContractHTMLBlock extends CXBaseBlock {
    type: 'contract_html_block';
    @observable contract: IContract;

    constructor(
        block: IContractHTMLBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.contract = block.contract;
    }

    @computed
    get blockInputValidations() {
        const validations = {};
        const { variablesValues } = this;
        const clientMustFill = this.contract.fields_metadata.filter(
            smartField => {
                return (
                    smartField.fill_type === 'client_required' &&
                    !variablesValues[smartField.connected_variable_id]
                );
            }
        ).length;

        if (clientMustFill) {
            validations[ContractBlockErrors.ClientMustFill] = clientMustFill;
        }

        return validations;
    }

    isManualEdited(variableId: string) {
        return this.flowRevision.variablesMap[variableId]?.is_manual_value;
    }

    @computed
    get variablesValues() {
        const values = {};
        this.contract.fields_metadata.forEach(data => {
            values[data.connected_variable_id] =
                this.rootStore.flowStore.variables[data.connected_variable_id]
                    ?.value ??
                data.default_value ??
                undefined;
        });
        return values;
    }

    getField(fieldId) {
        return this.contract.fields_metadata.find(
            field => field.field_id === fieldId
        );
    }

    @computed
    get fieldsMap() {
        const result = {};
        this.contract.fields_metadata.forEach((field: ISmartField) => {
            result[field.field_id] = field;
        });
        return result;
    }

    getFieldValue(fieldId) {
        const { flowStore, flowViewUiStore } = this.rootStore;

        const fieldData = this.getField(fieldId);
        const fieldVariable =
            flowStore.variables[fieldData.connected_variable_id];
        const value = fieldVariable?.value;
        if (
            fieldVariable?.data_source_type === 'invoice' &&
            this.flow.hasInvoiceBlock &&
            flowViewUiStore.previewContext.isPreviewServiceSelected
        ) {
            const cxInvoiceBlock =
                this.flowRevision.invoiceContainerBlock.invoiceSummaryBlock;
            return `$${previewVSmartFieldsValues[
                fieldVariable?.data_source_property
            ](cxInvoiceBlock)}`;
        }

        if (
            fieldVariable?.data_source_type !== 'flow' ||
            value ||
            !uuidValidate(fieldVariable?.data_source_property)
        ) {
            return value;
        }

        const sourceVariable =
            flowStore.variables[fieldVariable?.data_source_property];

        if (fieldData.data_source_sub_property && sourceVariable?.value) {
            return sourceVariable?.value[fieldData.data_source_sub_property];
        }

        const block = flowStore.blocks[sourceVariable?.connected_block_id];
        if (block && block.type === 'question_block') {
            return (block as CXQuestionBlock).getFieldValue(
                sourceVariable?.value
            );
        }

        return sourceVariable?.value;
    }
}
